import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Save, ArrowLeft, Scan } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PhotoUploader from '../../components/PhotoUploader';
import BarcodeScanner from '../../components/BarcodeScanner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isStaff } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    brand: '',
    image_url: '',
    price_detail: '',
    price_semi_gros: '',
    price_gros: '',
    cost_price: '',
    vip_margin: '',
    quantity_semi_gros: 6,
    quantity_gros: 12,
    stock: '',
    is_available: true,
    barcode: '',
    is_published: true,
    preorder_enabled: false,
    preorder_expected_date: '',
    preorder_qty: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchingProduct, setFetchingProduct] = useState(isEditing);
  const [showScanner, setShowScanner] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [newCategory, setNewCategory] = useState(false);
  const [newBrand, setNewBrand] = useState(false);

  useEffect(() => {
    if (!isStaff) {
      navigate('/connexion');
      return;
    }

    // Fetch categories and brands
    Promise.all([
      axios.get(`${API_URL}/api/products/meta/categories`),
      axios.get(`${API_URL}/api/products/meta/brands`),
    ]).then(([catRes, brandRes]) => {
      setCategories(catRes.data);
      setBrands(brandRes.data);
    }).catch(() => {});

    if (isEditing) {
      fetchProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isStaff, navigate, isEditing]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/${id}`);
      setFormData({
        name: response.data.name || '',
        description: response.data.description || '',
        category: response.data.category || '',
        brand: response.data.brand || '',
        image_url: response.data.image_url || '',
        price_detail: response.data.price_detail || '',
        price_semi_gros: response.data.price_semi_gros || '',
        price_gros: response.data.price_gros || '',
        cost_price: response.data.cost_price ?? '',
        vip_margin: response.data.vip_margin ?? '',
        quantity_semi_gros: response.data.quantity_semi_gros || 6,
        quantity_gros: response.data.quantity_gros || 12,
        stock: response.data.stock || '',
        is_available: response.data.is_available ?? true,
        barcode: response.data.barcode || '',
        is_published: response.data.is_published ?? true,
        preorder_enabled: !!response.data.preorder_enabled,
        preorder_expected_date: response.data.preorder_expected_date || '',
        preorder_qty: response.data.preorder_qty ?? ''
      });
    } catch (error) {
      // Error shown via loading=false + null data check in UI
      setError('Produit non trouvé');
    } finally {
      setFetchingProduct(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handlePhotoUpload = (url) => {
    setFormData({ ...formData, image_url: url });
  };

  const handleBarcodeScan = (barcode) => {
    setFormData({ ...formData, barcode });
    setShowScanner(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        price_detail: parseFloat(formData.price_detail),
        price_semi_gros: parseFloat(formData.price_semi_gros),
        price_gros: parseFloat(formData.price_gros),
        cost_price: isAdmin && formData.cost_price !== '' && formData.cost_price !== null
          ? parseFloat(formData.cost_price)
          : null,
        vip_margin: isAdmin && formData.vip_margin !== '' && formData.vip_margin !== null
          ? parseFloat(formData.vip_margin)
          : null,
        quantity_semi_gros: parseInt(formData.quantity_semi_gros),
        quantity_gros: parseInt(formData.quantity_gros),
        stock: parseInt(formData.stock || 0),
        barcode: formData.barcode || null,
        is_published: formData.preorder_enabled ? !!formData.is_published : true,
        preorder_enabled: !!formData.preorder_enabled,
        preorder_expected_date: formData.preorder_enabled ? (formData.preorder_expected_date || null) : null,
        preorder_qty: formData.preorder_enabled ? parseInt(formData.preorder_qty || 0) : 0
      };

      if (formData.preorder_enabled && !formData.preorder_expected_date) {
        setError("Indiquez la date d'arrivée prévue de la précommande");
        setLoading(false);
        return;
      }

      if (isEditing) {
        await axios.put(`${API_URL}/api/products/${id}`, payload, {
          
        });
      } else {
        await axios.post(`${API_URL}/api/products`, payload, {
          
        });
      }

      navigate('/admin/produits');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg || e).join(' '));
      } else {
        setError('Erreur lors de la sauvegarde');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isStaff) return null;

  if (fetchingProduct) {
    return (
      <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16 flex items-center justify-center">
        <div className="animate-pulse text-[color:var(--color-primary)]">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin/produits" className="text-zinc-500 hover:text-white text-sm mb-2 inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Retour aux produits
          </Link>
          <h1 
            className="text-3xl text-white font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {isEditing ? 'Modifier le Produit' : 'Nouveau Produit'}
          </h1>
        </div>

        {/* Barcode Scanner Modal */}
        {showScanner && (
          <BarcodeScanner
            onScan={handleBarcodeScan}
            onClose={() => setShowScanner(false)}
          />
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Basic Info */}
            <div className="space-y-6">
              <div className="bg-[color:var(--color-surface)] border border-white/10 p-6">
                <h3 className="text-white mb-4">Informations Générales</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Nom du produit *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                      placeholder="Ex: Oud Royal"
                      data-testid="product-name-input"
                    />
                  </div>

                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Marque *</label>
                    {newBrand ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name="brand"
                          value={formData.brand}
                          onChange={handleChange}
                          required
                          className="flex-1 bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                          placeholder="Nouvelle marque"
                          data-testid="product-brand-input"
                        />
                        <button
                          type="button"
                          onClick={() => { setNewBrand(false); setFormData({...formData, brand: ''}); }}
                          className="px-3 border border-white/10 text-zinc-400 hover:text-white transition-colors text-sm"
                        >
                          Liste
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <select
                          name="brand"
                          value={formData.brand}
                          onChange={handleChange}
                          required
                          className="flex-1 bg-[color:var(--color-surface)] border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                          data-testid="product-brand-select"
                        >
                          <option value="">Sélectionner...</option>
                          {brands.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => { setNewBrand(true); setFormData({...formData, brand: ''}); }}
                          className="px-3 border border-[color:var(--color-primary)] text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)] hover:text-black transition-colors text-sm whitespace-nowrap"
                          data-testid="new-brand-btn"
                        >
                          + Nouveau
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Catégorie *</label>
                    {newCategory ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          required
                          className="flex-1 bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                          placeholder="Nouvelle catégorie"
                          data-testid="product-category-input"
                        />
                        <button
                          type="button"
                          onClick={() => { setNewCategory(false); setFormData({...formData, category: ''}); }}
                          className="px-3 border border-white/10 text-zinc-400 hover:text-white transition-colors text-sm"
                        >
                          Liste
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          required
                          className="flex-1 bg-[color:var(--color-surface)] border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                          data-testid="product-category-select"
                        >
                          <option value="">Sélectionner...</option>
                          {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => { setNewCategory(true); setFormData({...formData, category: ''}); }}
                          className="px-3 border border-[color:var(--color-primary)] text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)] hover:text-black transition-colors text-sm whitespace-nowrap"
                          data-testid="new-category-btn"
                        >
                          + Nouveau
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Barcode */}
                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Code Barre (EAN)</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="barcode"
                        value={formData.barcode}
                        onChange={handleChange}
                        className="flex-1 bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                        placeholder="Scanner ou entrer manuellement"
                        data-testid="product-barcode-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowScanner(true)}
                        className="px-4 border border-[color:var(--color-primary)] text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)] hover:text-black transition-colors"
                        data-testid="scan-barcode-btn"
                      >
                        <Scan className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                      className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none resize-none"
                      placeholder="Description du parfum..."
                      data-testid="product-description-input"
                    />
                  </div>
                </div>
              </div>

              {/* Photo Upload */}
              <div className="bg-[color:var(--color-surface)] border border-white/10 p-6">
                <h3 className="text-white mb-4">Photo du Produit</h3>
                <PhotoUploader
                  onUpload={handlePhotoUpload}
                  currentImage={formData.image_url}
                />
              </div>
            </div>

            {/* Right Column - Pricing & Stock */}
            <div className="space-y-6">
              {/* Pricing - personnel autorisé */}
              {isStaff ? (
                <div className="bg-[color:var(--color-surface)] border border-white/10 p-6">
                  <h3 className="text-white mb-4">Tarification</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Prix Détail (€) *</label>
                      <input
                        type="number"
                        step="0.01"
                        name="price_detail"
                        value={formData.price_detail}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                        placeholder="0.00"
                        data-testid="product-price-detail-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Prix Semi-gros (€) *</label>
                        <input
                          type="number"
                          step="0.01"
                          name="price_semi_gros"
                          value={formData.price_semi_gros}
                          onChange={handleChange}
                          required
                          className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                          placeholder="0.00"
                          data-testid="product-price-semigros-input"
                        />
                      </div>
                      <div>
                        <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Qté min Semi-gros</label>
                        <input
                          type="number"
                          name="quantity_semi_gros"
                          value={formData.quantity_semi_gros}
                          onChange={handleChange}
                          className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                          data-testid="product-qty-semigros-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Prix Gros (€) *</label>
                        <input
                          type="number"
                          step="0.01"
                          name="price_gros"
                          value={formData.price_gros}
                          onChange={handleChange}
                          required
                          className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                          placeholder="0.00"
                          data-testid="product-price-gros-input"
                        />
                      </div>
                      <div>
                        <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Qté min Gros</label>
                        <input
                          type="number"
                          name="quantity_gros"
                          value={formData.quantity_gros}
                          onChange={handleChange}
                          className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                          data-testid="product-qty-gros-input"
                        />
                      </div>
                    </div>

                    {/* Cost price + VIP margin — admin internal use only */}
                    {isAdmin && (
                    <div className="pt-4 border-t border-white/5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-amber-500 text-xs uppercase tracking-widest mb-2 block">
                            Prix d'achat (€) — interne
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            name="cost_price"
                            value={formData.cost_price}
                            onChange={handleChange}
                            className="w-full bg-transparent border border-amber-500/30 focus:border-amber-500 text-white px-4 py-3 outline-none"
                            placeholder="(facultatif)"
                            data-testid="product-cost-price-input"
                          />
                        </div>
                        <div>
                          <label className="text-amber-500 text-xs uppercase tracking-widest mb-2 block">
                            Marge VIP (€) — défaut 1€
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            name="vip_margin"
                            value={formData.vip_margin}
                            onChange={handleChange}
                            className="w-full bg-transparent border border-amber-500/30 focus:border-amber-500 text-white px-4 py-3 outline-none"
                            placeholder="1.00 par défaut"
                            data-testid="product-vip-margin-input"
                          />
                        </div>
                      </div>
                      {formData.cost_price && parseFloat(formData.cost_price) > 0 && (() => {
                        const cp = parseFloat(formData.cost_price);
                        const m = formData.vip_margin === '' || formData.vip_margin == null
                          ? 1
                          : parseFloat(formData.vip_margin);
                        const vip = (cp + (isFinite(m) ? m : 1)).toFixed(2);
                        return (
                          <p className="text-zinc-500 text-xs mt-3" data-testid="vip-preview">
                            💎 Prix VIP calculé : <span className="text-[color:var(--color-primary)] font-medium">{vip}€</span>
                            <span className="text-zinc-600"> (achat {cp.toFixed(2)}€ + marge {(isFinite(m) ? m : 1).toFixed(2)}€)</span>
                            {formData.price_gros && parseFloat(formData.price_gros) > 0 && (
                              <span className="ml-3">
                                📈 Marge sur prix gros : <span className="text-green-400">
                                  {((parseFloat(formData.price_gros) - cp) / parseFloat(formData.price_gros) * 100).toFixed(1)}%
                                </span>
                              </span>
                            )}
                          </p>
                        );
                      })()}
                      <p className="text-zinc-600 text-[10px] mt-2">
                        💡 La marge VIP peut être réduite pour les petits produits ou augmentée pour les pièces premium. Laissez vide pour utiliser 1€.
                      </p>
                    </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-[color:var(--color-surface)] border border-white/10 p-6">
                  <h3 className="text-white mb-2">Tarification</h3>
                  <p className="text-zinc-500 text-sm">Les prix sont gérés par l'administrateur.</p>
                </div>
              )}

              <div className="bg-[color:var(--color-surface)] border border-white/10 p-6">
                <h3 className="text-white mb-4">Stock</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Quantité en stock *</label>
                    <input
                      type="number"
                      name="stock"
                      value={formData.stock}
                      onChange={handleChange}
                      required={!formData.preorder_enabled}
                      className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                      placeholder="0"
                      data-testid="product-stock-input"
                    />
                  </div>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_available"
                      checked={formData.is_available}
                      onChange={handleChange}
                      className="w-5 h-5 accent-[color:var(--color-primary)]"
                      data-testid="product-available-input"
                    />
                    <span className="text-zinc-400">Produit disponible à la vente</span>
                  </label>
                </div>
              </div>

              {/* Précommande fournisseur */}
              <div className={`border p-6 ${formData.preorder_enabled ? 'border-[color:rgb(var(--primary-rgb)/0.40)] bg-[color:rgb(var(--primary-rgb)/0.05)]' : 'border-white/10 bg-[color:var(--color-surface)]'}`}
                data-testid="preorder-section">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="preorder_enabled"
                    checked={formData.preorder_enabled}
                    onChange={(e) => setFormData({
                      ...formData,
                      preorder_enabled: e.target.checked,
                      is_published: e.target.checked ? (isEditing ? formData.is_published : false) : true,
                    })}
                    className="w-5 h-5 accent-[color:var(--color-primary)]"
                    data-testid="product-preorder-toggle"
                  />
                  <span className="text-white">Précommande fournisseur (arrivage à venir)</span>
                </label>
                {formData.preorder_enabled && (
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Date d'arrivée prévue *</label>
                        <input
                          type="date"
                          name="preorder_expected_date"
                          value={formData.preorder_expected_date}
                          onChange={handleChange}
                          className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                          data-testid="product-preorder-date-input"
                        />
                      </div>
                      <div>
                        <label className="text-zinc-500 text-xs uppercase tracking-widest mb-2 block">Quantité commandée</label>
                        <input
                          type="number"
                          min="0"
                          name="preorder_qty"
                          value={formData.preorder_qty}
                          onChange={handleChange}
                          className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                          placeholder="0"
                          data-testid="product-preorder-qty-input"
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!formData.is_published}
                        onChange={(e) => setFormData({ ...formData, is_published: !e.target.checked })}
                        className="w-5 h-5 accent-[color:var(--color-primary)]"
                        data-testid="product-hidden-until-received-input"
                      />
                      <span className="text-zinc-400 text-sm">
                        Nouveau produit : masquer du catalogue jusqu'à validation de la réception
                      </span>
                    </label>
                    <p className="text-zinc-500 text-xs">
                      Le produit apparaît dans l'onglet client « Précommande ». Les clients réservent des pièces,
                      livrées après votre validation de la réception (Admin › Précommandes).
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-center" data-testid="product-form-error">{error}</p>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Link to="/admin/produits" className="px-6 py-3 border border-white/10 text-zinc-400 hover:border-white/20 transition-colors">
              Annuler
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-6 py-3 hover:bg-[color:var(--color-primary-hover)] transition-colors disabled:opacity-50"
              data-testid="product-submit-btn"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProductForm;
